import { useCore } from '@/composables/core'
import { FEATURE_FLAGS } from '@/lib/consts.ts'
import { EVENT_ACTIONS, SCREEN_NAMES } from '@/lib/analytics/consts.ts'
import { useDefaultRedirect } from '@/composables/default-redirect'
import { useAnalytics } from '@/composables/analytics'
import { useLoadingScreen } from '@/composables/loading-screen'
import { useDevLogger } from '@/composables/dev-logger'
import { createBooksyCardReader, destroyBooksyCardReader } from '@/composables/payments/booksy-card-reader'
import { useExtole } from '@/composables/extole'
import { useNativeIdentityService } from '@/composables/native-identity'
import { destroyChat } from '@/composables/chat'
import { useInTouchChat } from '@/composables/in-touch-chat/use-in-touch-chat'
import { destroyBooksyAssistant } from '@/components/business-setup-agent/composables'
import { createIterableUserInstance, destroyIterableUserInstance } from '@/composables/marketing/iterable-embedded-messaging'
import { useEppo } from '@/composables/eppo'
import { useRedirection } from '@/composables/default-redirect/redirection'
import { LOGOUT_REASONS, type LogoutReason } from './consts'
export * from './consts'

export interface AuthenticationLoginOptions {
  shouldAutoloadBusiness?: boolean
}

export interface AuthenticationLogoutOptions {
  reason?: LogoutReason
}

export type UseAuthenticationReturn = {
  login: (
    identity?: unknown,
    redirect?: boolean,
    options?: AuthenticationLoginOptions,
  ) => Promise<unknown>
  logout: (
    redirect?: boolean,
    options?: AuthenticationLogoutOptions,
  ) => Promise<unknown>
}

/** Vuex root state slice used for logout analytics */
type StoreStateWithIdentity = {
  identity?: { loginType?: string }
}

async function login (
  identity?: unknown,
  redirect = true,
  options: AuthenticationLoginOptions = {}
): Promise<unknown> {
  const { store } = useCore()
  const defaultRedirect = useDefaultRedirect()
  const logger = useDevLogger('login')
  const nativeIdentity = useNativeIdentityService()

  const {
    shouldAutoloadBusiness = true,
  } = options

  logger.debug(identity)
  store.dispatch('identity/login', identity)

  if (shouldAutoloadBusiness) {
    try {
      // BIL-553 - in the future that bit will be moved to billing-microservice
      const redirection = useRedirection().get() as { query?: Record<string, unknown> } | undefined
      const businessId = redirection?.query?.active_business_id
      businessId && await store.dispatch('identity/selectBusiness', businessId)

      await store.dispatch('business/autoloadBusiness')
    } catch (e: unknown) {
      logger.error(e)

      const status = (e as { response?: { status?: number } })?.response?.status
      if (status !== undefined && status >= 400) {
        logout(redirect, { reason: LOGOUT_REASONS.BUSINESS_DETAILS_ERROR })
        return Promise.reject(e)
      }
    }
  }

  if (nativeIdentity.isAvailable()) {
    nativeIdentity.login()
  }

  try {
    await createBooksyCardReader()
  } catch (e: unknown) {
    logger.error(e)
  }

  if (store.getters['app/isAndroidTablet']) {
    void import('../google-play/index.ts').then(({ useGooglePlay }) => {
      logger.debug('setting up google play subscription autorestore...')
      const { setupSubscriptionAutorestore } = useGooglePlay()
      setupSubscriptionAutorestore()
    })
  }

  try {
    if (useEppo().ff(FEATURE_FLAGS.Feature_IterableEmbeddedMessaging)) {
      await createIterableUserInstance()
    }
  } catch (e: unknown) {
    void e
  }

  useAnalytics().identify()

  // shortcut
  if (!redirect) return

  // redirect
  return defaultRedirect()
}

async function logout (
  redirect = true,
  options: AuthenticationLogoutOptions = {}
): Promise<unknown> {
  const { store } = useCore()
  const analytics = useAnalytics()
  const defaultRedirect = useDefaultRedirect()
  const loadingScreen = useLoadingScreen()
  const logger = useDevLogger('logout')
  const nativeIdentity = useNativeIdentityService()

  const loginType = (store.state as StoreStateWithIdentity).identity?.loginType

  useAnalytics().reset()

  logger.debug(redirect)

  loadingScreen.show()

  // log user out and invalidate currently used token
  await store.dispatch('api/logout')

  try {
    if (nativeIdentity?.isAvailable()) {
      await nativeIdentity.logout()
    }
  } catch (e: unknown) {
    logger.error(e)
  }

  try {
    await destroyBooksyCardReader()
  } catch (e: unknown) {
    logger.error(e)
  }

  try {
    await destroyChat()
  } catch (e: unknown) {
    logger.error(e)
  }

  try {
    await useInTouchChat().disconnect()
  } catch (e: unknown) {
    logger.error(e)
  }

  try {
    destroyBooksyAssistant()
  } catch (e: unknown) {
    logger.error(e)
  }

  analytics.dispatch(analytics.EVENTS.LOGOUT)
  analytics.dispatch(analytics.EVENTS.LOGIN_LOGOUT_ACTION, {
    eventAction: EVENT_ACTIONS.USER_LOGGED_OUT,
    screenName: SCREEN_NAMES.LOGOUT_MODAL,
    eventReason: options?.reason,
    loginMethod: loginType,
  })

  await Promise.all([
    store.dispatch('identity/logout'),
    store.dispatch('business/reset'),
  ])

  useExtole().logout()

  // Intentional avoid ff here in case of instance was created and flag changed to false
  try {
    destroyIterableUserInstance()
  } catch (e: unknown) {
    void e
  }

  loadingScreen.hide()

  // shortcut
  if (!redirect) return

  // redirect
  return defaultRedirect()
}

export const useAuthentication = (): UseAuthenticationReturn => {
  return {
    login,
    logout,
  }
}
